export const AUDIO_MIXER_DEFAULT_VOLUME = 0.75;

export const AUDIO_MIXER_CATEGORIES = [
  "alerts",
  "agendaAlerts",
  "music",
  "birthday",
  "timers",
  "widgets",
];

const AUDIO_MIXER_MUTED_KEY = "lessonLaunchpadAudioMixerMuted";
const AUDIO_MIXER_MUTE_EVENT = "lessonLaunchpadAudioMixerMuteChange";

export const clampAudioVolume = (value, fallback = AUDIO_MIXER_DEFAULT_VOLUME) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }
  return Math.max(0, Math.min(1, numericValue));
};

export const getDefaultAudioSettings = () =>
  AUDIO_MIXER_CATEGORIES.reduce((settings, category) => {
    settings[category] = AUDIO_MIXER_DEFAULT_VOLUME;
    return settings;
  }, {});

export const normalizeAudioSettings = (settings = {}) => {
  const normalized = getDefaultAudioSettings();
  AUDIO_MIXER_CATEGORIES.forEach((category) => {
    if (settings?.[category] != null) {
      normalized[category] = clampAudioVolume(settings[category]);
    }
  });
  return normalized;
};

export const resolveUserAudioSettings = (userSettings = {}) => {
  const normalized = normalizeAudioSettings(userSettings?.audioSettings);
  if (userSettings?.audioSettings?.alerts == null && userSettings?.alertVolume != null) {
    normalized.alerts = clampAudioVolume(userSettings.alertVolume);
  }
  return normalized;
};

const resolveClassData = ({ classId, classData, classes }) => {
  if (classData) return classData;
  if (!classId || !Array.isArray(classes)) return null;
  return classes.find((candidate) => String(candidate?.classId) === String(classId)) || null;
};

export const getAudioMixerMuted = () => {
  if (typeof window === "undefined") return false;
  return window.sessionStorage?.getItem(AUDIO_MIXER_MUTED_KEY) === "true";
};

export const setAudioMixerMuted = (isMuted) => {
  if (typeof window === "undefined") return;
  window.sessionStorage?.setItem(AUDIO_MIXER_MUTED_KEY, isMuted ? "true" : "false");
  window.dispatchEvent(new CustomEvent(AUDIO_MIXER_MUTE_EVENT, { detail: { muted: Boolean(isMuted) } }));
};

export const subscribeAudioMixerMute = (callback) => {
  if (typeof window === "undefined") return () => {};
  const handler = (event) => callback(Boolean(event?.detail?.muted));
  window.addEventListener(AUDIO_MIXER_MUTE_EVENT, handler);
  return () => window.removeEventListener(AUDIO_MIXER_MUTE_EVENT, handler);
};

export const resolveCategoryVolume = ({
  category,
  classId,
  classData,
  classes,
  user,
}) => {
  if (!AUDIO_MIXER_CATEGORIES.includes(category)) {
    return AUDIO_MIXER_DEFAULT_VOLUME;
  }

  const resolvedClass = resolveClassData({ classId, classData, classes });
  const classVolume = resolvedClass?.audioSettings?.[category];
  if (classVolume != null) {
    return clampAudioVolume(classVolume);
  }

  return resolveUserAudioSettings(user?.settings)[category];
};

export const resolveEffectiveVolume = ({
  category,
  classId,
  classData,
  classes,
  user,
  volumeMultiplier = 1,
  fallback = AUDIO_MIXER_DEFAULT_VOLUME,
} = {}) => {
  if (getAudioMixerMuted()) {
    return 0;
  }

  const masterVolume = clampAudioVolume(user?.settings?.masterVolume, fallback);
  const categoryVolume = resolveCategoryVolume({
    category,
    classId,
    classData,
    classes,
    user,
  });

  return clampAudioVolume(
    masterVolume * categoryVolume * clampAudioVolume(volumeMultiplier, 1),
    fallback
  );
};

export const createManagedAudio = (src, options = {}) => {
  if (!src) return null;
  const audio = new Audio(src);
  audio.volume = resolveEffectiveVolume(options);
  return audio;
};

export const playManagedAudio = (src, options = {}) => {
  const audio = createManagedAudio(src, options);
  if (!audio) {
    return Promise.resolve(null);
  }
  return audio.play().then(() => audio);
};

export const applyVolumeToYouTubePlayer = (player, options = {}) => {
  if (!player || typeof player.setVolume !== "function") {
    return {
      applied: false,
      volumePercent: 0,
      wantedAudible: false,
      playerMuted: null,
    };
  }

  const volumePercent = Math.max(0, Math.min(100, Math.round(resolveEffectiveVolume(options) * 100)));
  const wantedAudible = volumePercent > 0;
  player.setVolume(volumePercent);

  if (wantedAudible) {
    if (typeof player.unMute === "function") {
      player.unMute();
    }
  } else if (typeof player.mute === "function") {
    player.mute();
  }

  const playerMuted = typeof player.isMuted === "function" ? Boolean(player.isMuted()) : null;
  return {
    applied: true,
    volumePercent,
    wantedAudible,
    playerMuted,
  };
};

export const isYouTubePlayerMutedAgainstVolume = (player, options = {}) => {
  if (!player || typeof player.isMuted !== "function") return false;
  return Boolean(player.isMuted()) && resolveEffectiveVolume(options) > 0;
};
